import { Link } from "react-router-dom";
import { Phone, Star, Shield, Clock, DollarSign, ChevronRight, Sparkles, Home as HomeIcon, Building2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const benefits = [
  {
    icon: Shield,
    title: "Trusted & Insured",
    description: "Fully insured and background-checked cleaners you can trust in your home.",
  },
  {
    icon: Clock,
    title: "Reliable Service",
    description: "We show up on time, every time. Count on us for consistent quality.",
  },
  {
    icon: DollarSign,
    title: "Affordable Rates",
    description: "Competitive pricing with no hidden fees. Get a free quote today.",
  },
];

const services = [
  {
    icon: HomeIcon,
    title: "Standard Cleaning",
    description: "Regular house cleaning to keep your home fresh and tidy week after week.",
    link: "/services#standard",
  },
  {
    icon: Sparkles,
    title: "Deep Cleaning",
    description: "Thorough top-to-bottom cleaning for a truly spotless home.",
    link: "/services#deep",
  },
  {
    icon: Truck,
    title: "Move-In/Move-Out",
    description: "Get your deposit back or start fresh in your new place.",
    link: "/services#move",
  },
  {
    icon: Building2,
    title: "Office Cleaning",
    description: "Professional cleaning services for businesses of all sizes.",
    link: "/services#office",
  },
];

const testimonials = [
  {
    name: "Sarah M.",
    location: "Lubbock, TX",
    text: "West Texas Sparkle has been cleaning my home for 6 months now. They're always on time, thorough, and so friendly. My house has never looked better!",
    rating: 5,
  },
  {
    name: "Mike T.",
    location: "Wolfforth, TX",
    text: "Used them for a move-out clean and got my full deposit back. They did an amazing job on every room. Highly recommend!",
    rating: 5,
  },
  {
    name: "Jennifer L.",
    location: "Lubbock, TX",
    text: "Our office has never been cleaner. The team is professional, detail-oriented, and always goes the extra mile. Great value!",
    rating: 5,
  },
];

const faqs = [
  {
    question: "What cleaning services do you offer in Lubbock?",
    answer: "We offer standard house cleaning, deep cleaning, move-in/move-out cleaning, and commercial office cleaning services throughout Lubbock, Wolfforth, and Levelland, TX.",
  },
  {
    question: "How much does house cleaning cost in Lubbock?",
    answer: "Our pricing depends on the size of your home and the type of cleaning needed. Contact us for a free, no-obligation quote tailored to your specific needs.",
  },
  {
    question: "Do I need to be home during the cleaning?",
    answer: "No, you don't need to be home. Many of our clients provide a key or garage code. All our cleaners are background-checked and fully insured for your peace of mind.",
  },
  {
    question: "What cleaning products do you use?",
    answer: "We use professional-grade, eco-friendly cleaning products that are safe for your family and pets. If you have specific product preferences, just let us know!",
  },
  {
    question: "How do I schedule a cleaning?",
    answer: "Scheduling is easy! Call us at (806) 555-8123, or fill out our online contact form for a free quote. We'll work around your schedule to find a convenient time.",
  },
];

export default function Home() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-sky-50 to-white hero-noise">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 text-sky-700 text-sm font-medium mb-6 animate-fade-in-up">
                <Sparkles className="w-4 h-4" />
                Serving Lubbock, Wolfforth & Levelland
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6 animate-fade-in-up animate-delay-100">
                Professional Cleaning
                <span className="text-sky-600"> You Can Trust</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl animate-fade-in-up animate-delay-200">
                Transform your home or office with West Texas Sparkle. Reliable, affordable, 
                and thorough cleaning services for residential and commercial spaces.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-300">
                <a href="tel:8065558123">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto btn-primary bg-sky-600 hover:bg-sky-700 text-white rounded-full h-14 px-8 text-lg font-semibold"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-sky-200 hover:border-sky-300 hover:bg-sky-50 text-sky-700 rounded-full h-14 px-8 text-lg font-semibold"
                    data-testid="hero-quote-btn"
                  >
                    Get a Free Quote
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in-up animate-delay-400">
              <div className="absolute -inset-4 bg-sky-200/30 rounded-3xl blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/4239031/pexels-photo-4239031.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Professional cleaning service in Lubbock TX"
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Choose West Texas Sparkle?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We're committed to providing the best cleaning experience in West Texas.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card 
                key={benefit.title} 
                className="service-card bg-white border-slate-100 p-2"
                data-testid={`benefit-card-${index}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-sky-100 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Cleaning Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From routine cleaning to deep scrubs, we've got your needs covered.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link to={service.link} key={service.title}>
                <Card 
                  className="service-card h-full bg-white border-slate-100 hover:border-sky-200 cursor-pointer group"
                  data-testid={`service-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-sky-50 group-hover:bg-sky-100 flex items-center justify-center mb-4 transition-colors">
                      <service.icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 group-hover:text-sky-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button 
                variant="outline" 
                className="border-sky-200 hover:border-sky-300 hover:bg-sky-50 text-sky-700 rounded-full px-8"
                data-testid="view-all-services-btn"
              >
                View All Services
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it — hear from our happy customers.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="testimonial-card bg-white border-slate-100 overflow-hidden"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-6 pt-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-sparkle-gold text-sparkle-gold" />
                    ))}
                  </div>
                  <p className="text-slate-700 leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                      <span className="font-semibold text-sky-700">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Got questions? We've got answers.
            </p>
          </div>
          <Accordion type="single" collapsible className="faq-accordion space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border border-slate-100 px-6 data-[state=open]:shadow-md transition-shadow"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:text-sky-700 py-5 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-sky-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for a Sparkling Clean Home?
          </h2>
          <p className="text-sky-100 text-lg mb-8 max-w-2xl mx-auto">
            Get your free quote today and discover why Lubbock families trust West Texas Sparkle 
            for all their cleaning needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8065558123">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-white text-sky-700 hover:bg-sky-50 rounded-full h-14 px-8 text-lg font-semibold shadow-lg"
                data-testid="cta-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                (806) 555-8123
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/30 bg-transparent hover:bg-white/10 text-white rounded-full h-14 px-8 text-lg font-semibold"
                data-testid="cta-quote-btn"
              >
                Request a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
